import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Edit, 
  Trash2, 
  Calendar,
  MapPin,
  Users,
  Bed,
  Bath,
  DollarSign,
  Clock,
  Star,
  Building2,
  Wifi,
  Car,
  Coffee,
  Waves,
  Dumbbell,
  Sparkles,
  Utensils,
  Home,
  Building
} from "lucide-react";
import SaudiRiyal from "@/components/shared/saudi-riyal";
import { Property as ApiProperty, propertiesService } from "@/services/propertiesService";
import { PropertyType as ApiPropertyType } from "@/services/propertyTypesService";
import { propertyMediaService, PropertyMedia } from "@/services/propertyMediaService";
import { useState, useEffect, useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";

interface PropertyViewProps {
  isOpen: boolean;
  onClose: () => void;
  property: ApiProperty | null;
  onEdit: (property: ApiProperty) => void;
  onDelete: (property: ApiProperty) => void;
}

export function PropertyView({ 
  isOpen, 
  onClose, 
  property, 
  onEdit, 
  onDelete 
}: PropertyViewProps) {
  const { t } = useTranslation();
  const [detailedProperty, setDetailedProperty] = useState<ApiProperty | null>(null);
  const [loading, setLoading] = useState(false);
  const [media, setMedia] = useState<PropertyMedia[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [imageBlobUrls, setImageBlobUrls] = useState<Record<string, string>>({});
  const [imagesLoading, setImagesLoading] = useState(false);

  // Fetch detailed property data when modal opens
  useEffect(() => {
    const fetchPropertyDetails = async () => {
      if (isOpen && property?.id) {
        setLoading(true);
        setMediaLoading(true);
        // Reset media when opening
        setMedia([]);
        
        try {
          console.log('🔧 Fetching property details for ID:', property.id);
          const propertyId = typeof property.id === 'string' ? parseInt(property.id) : property.id;
          
          // Fetch property details and media in parallel
          const [propertyResponse, mediaResponse] = await Promise.all([
            propertiesService.getPropertyById(propertyId),
            propertyMediaService.getPropertyMedia(propertyId)
          ]);
          
          if (propertyResponse.success && propertyResponse.data) {
            console.log('🔧 Property details loaded:', propertyResponse.data);
            setDetailedProperty(propertyResponse.data);
          } else {
            console.error('🔧 Failed to load property details:', propertyResponse.message);
            // Fallback to basic property data
            setDetailedProperty(property);
          }
          
          console.log('🔧 Media response received:', mediaResponse);
          console.log('🔧 Media response.success:', mediaResponse.success);
          console.log('🔧 Media response.data:', mediaResponse.data);
          
          if (mediaResponse.success && mediaResponse.data) {
            console.log('🔧 Property media loaded:', mediaResponse.data);
            console.log('🔧 Property media isArray:', Array.isArray(mediaResponse.data));
            console.log('🔧 Property media length:', Array.isArray(mediaResponse.data) ? mediaResponse.data.length : 'not an array');
            
            // Ensure we have an array
            const mediaArray = Array.isArray(mediaResponse.data) ? mediaResponse.data : [];
            console.log('🔧 Setting media state with array of length:', mediaArray.length);
            console.log('🔧 First media item:', mediaArray[0]);
            setMedia(mediaArray);
          } else {
            console.error('🔧 Failed to load property media:', mediaResponse.message);
            console.error('🔧 Full media response:', JSON.stringify(mediaResponse, null, 2));
            setMedia([]);
          }
        } catch (error) {
          console.error('🔧 Error fetching property details:', error);
          console.error('🔧 Error details:', error);
          // Fallback to basic property data
          setDetailedProperty(property);
          setMedia([]);
        } finally {
          setLoading(false);
          setMediaLoading(false);
        }
      } else {
        // Reset when dialog closes
        setMedia([]);
        setDetailedProperty(null);
      }
    };

    fetchPropertyDetails();
  }, [isOpen, property?.id]);

  // Helper function to fetch image as blob with authentication
  const fetchImageAsBlob = async (url: string, mediaId: number): Promise<string | null> => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('No auth token available for image fetch');
        return null;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        // Don't use credentials: 'include' to avoid CORS issues with wildcard origins
        // The Authorization header is sufficient for authentication
      });

      if (!response.ok) {
        console.error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        return null;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      return blobUrl;
    } catch (error) {
      console.error('Error fetching image as blob:', error);
      return null;
    }
  };

  // Fetch all images as blobs when media changes
  useEffect(() => {
    if (!media || media.length === 0) {
      setImagesLoading(false);
      return;
    }

    const fetchAllImages = async () => {
      setImagesLoading(true);
      const newBlobUrls: Record<string, string> = {};
      
      // Clean up old blob URLs first
      setImageBlobUrls(prev => {
        Object.values(prev).forEach(url => URL.revokeObjectURL(url));
        return {};
      });
      
      for (const mediaItem of media) {
        if (mediaItem.media_type === 'image' && mediaItem.url) {
          // Normalize URL first
          let imageUrl = mediaItem.url;
          if (!imageUrl.startsWith('http')) {
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
            imageUrl = imageUrl.startsWith('/') ? `${apiBaseUrl}${imageUrl}` : `${apiBaseUrl}/${imageUrl}`;
          }

          const blobUrl = await fetchImageAsBlob(imageUrl, mediaItem.id);
          if (blobUrl) {
            newBlobUrls[mediaItem.id.toString()] = blobUrl;
            // Update state incrementally so images appear as they load
            setImageBlobUrls(prev => ({ ...prev, [mediaItem.id.toString()]: blobUrl }));
          }
        }
      }

      setImagesLoading(false);
    };

    fetchAllImages();

    // Cleanup function
    return () => {
      setImageBlobUrls(prev => {
        Object.values(prev).forEach(url => URL.revokeObjectURL(url));
        return {};
      });
      setImagesLoading(false);
    };
  }, [media]);

  // Early return after all hooks
  if (!property) return null;

  // Use detailed property data if available, otherwise fallback to basic property
  const displayProperty = detailedProperty || property;

  const getPropertyTypeName = () => {
    if (displayProperty.PropertyType) {
      return displayProperty.PropertyType.name_en;
    }
    return "Unknown";
  };

  const getPropertyTypeIcon = () => {
    if (displayProperty.PropertyType) {
      return displayProperty.PropertyType.icon;
    }
    return "🏠";
  };

  const getAmenityIcon = (amenity: string) => {
    const iconMap: Record<string, any> = {
      wifi: Wifi,
      parking: Car,
      kitchen: Coffee,
      airConditioning: Sparkles,
      swimmingPool: Waves,
      gym: Dumbbell,
      spa: Sparkles,
      restaurant: Utensils,
      roomService: Coffee,
      laundry: Coffee,
      balcony: Home,
      seaView: Waves,
      cityView: Building
    };
    return iconMap[amenity] || Coffee;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString: string) => {
    return timeString;
  };


  // Helper function to get image URL (blob URL if available, otherwise normalized URL)
  const getImageUrl = (mediaItem: PropertyMedia): string => {
    // First try blob URL (if we fetched it)
    if (imageBlobUrls[mediaItem.id.toString()]) {
      return imageBlobUrls[mediaItem.id.toString()];
    }

    // Otherwise, normalize the URL (no token in URL - authentication via Authorization header in blob fetch)
    let imageUrl = mediaItem.url || '';
    
    if (!imageUrl.startsWith('http')) {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
      imageUrl = imageUrl.startsWith('/') ? `${apiBaseUrl}${imageUrl}` : `${apiBaseUrl}/${imageUrl}`;
    }

    return imageUrl;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {loading ? (
              <Skeleton className="h-12 w-12 rounded" />
            ) : (
              <span className="text-3xl">{getPropertyTypeIcon()}</span>
            )}
            <div className="flex-1">
              {loading ? (
                <>
                  <Skeleton className="h-6 w-64 mb-2" />
                  <Skeleton className="h-4 w-32" />
                </>
              ) : (
                <>
                  <h2 className="text-xl font-bold">{displayProperty.name}</h2>
                  <p className="text-sm text-muted-foreground">{displayProperty.location}</p>
                </>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Property Images */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {t("media")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {mediaLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Skeleton className="h-8 w-8 rounded-full mr-2" />
                  <span className="text-sm text-muted-foreground">{t("loading")}...</span>
                </div>
              ) : media && media.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {media.map((mediaItem) => {
                    const blobUrl = imageBlobUrls[mediaItem.id.toString()];
                    const isImage = mediaItem.media_type === 'image';
                    const isLoading = isImage && !blobUrl && imagesLoading;
                    
                    return (
                      <div key={mediaItem.id} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                          {isLoading ? (
                            <div className="w-full h-full flex items-center justify-center">
                              <Skeleton className="w-full h-full" />
                            </div>
                          ) : isImage && blobUrl ? (
                            <img
                              src={blobUrl}
                              alt={mediaItem.alt_text || mediaItem.title || 'Property image'}
                              className="w-full h-full object-cover"
                              onLoad={() => {
                                console.log('✅ Image loaded successfully:', blobUrl);
                              }}
                              onError={(e) => {
                                console.error('❌ Image failed to load:', blobUrl);
                                console.error('❌ Media item:', mediaItem);
                              }}
                            />
                          ) : isImage ? (
                            <div className="w-full h-full flex items-center justify-center">
                              <Building2 className="h-12 w-12 text-muted-foreground opacity-50" />
                            </div>
                          ) : (
                            <video
                              src={blobUrl || getImageUrl(mediaItem)}
                              className="w-full h-full object-cover"
                              controls
                              onError={(e) => {
                                console.error('❌ Video failed to load');
                              }}
                            />
                          )}
                          {mediaItem.is_primary && (
                            <Badge className="absolute top-2 left-2 bg-primary">
                              <Star className="h-3 w-3 mr-1" />
                              {t("primaryImage")}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>{t("noMediaFound") || "No media found"}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {t("propertyName")} Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("propertyName")}</h4>
                  <p className="text-lg">{displayProperty.name}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("propertyType")}</h4>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getPropertyTypeIcon()}</span>
                    <span>{getPropertyTypeName()}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("propertyDescription")}</h4>
                <p className="text-sm">{displayProperty.description}</p>
              </div>
            </CardContent>
          </Card>

          {/* Location Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                {t("location")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("location")}</h4>
                  <p className="text-sm">{displayProperty.location}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("address")}</h4>
                  <p className="text-sm">{displayProperty.address}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("latitude")}</h4>
                  <p className="text-sm">{displayProperty.latitude}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("longitude")}</h4>
                  <p className="text-sm">{displayProperty.longitude}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                {t("price")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("fullPrice")}</h4>
                  <p className="text-2xl font-bold">
                    <SaudiRiyal 
                      amount={typeof displayProperty.full_price === 'string' ? parseFloat(displayProperty.full_price) : displayProperty.full_price} 
                      variant="bold_new" 
                      className="text-2xl" 
                    />
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("depositType")}</h4>
                  <p className="text-sm">{t(displayProperty.deposit_type)}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("depositValue")}</h4>
                  <p className="text-sm">
                    {displayProperty.deposit_type === 'fixed' ? (
                      <SaudiRiyal amount={displayProperty.deposit_value} variant="new" />
                    ) : `${displayProperty.deposit_value}%`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Capacity Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t("capacity")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <Users className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <div className="text-2xl font-bold">{displayProperty.capacity}</div>
                  <div className="text-sm text-muted-foreground">{t("capacity")}</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <Bed className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <div className="text-2xl font-bold">{displayProperty.bedrooms}</div>
                  <div className="text-sm text-muted-foreground">{t("bedrooms")}</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <Bath className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <div className="text-2xl font-bold">{displayProperty.bathrooms}</div>
                  <div className="text-sm text-muted-foreground">{t("bathrooms")}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Amenities */}
          <Card>
            <CardHeader>
              <CardTitle>{t("amenities")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {(() => {
                  // Parse amenities from JSON string or use array directly
                  let amenities: string[] = [];
                  try {
                    if (typeof displayProperty.amenities === 'string') {
                      amenities = JSON.parse(displayProperty.amenities);
                    } else if (Array.isArray(displayProperty.amenities)) {
                      amenities = displayProperty.amenities;
                    }
                  } catch (error) {
                    console.error('Error parsing amenities:', error);
                    amenities = [];
                  }
                  
                  return amenities.map((amenity) => {
                  const IconComponent = getAmenityIcon(amenity);
                  return (
                    <div key={amenity} className="flex items-center gap-2">
                      <IconComponent className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{t(amenity)}</span>
                    </div>
                  );
                  });
                })()}
              </div>
            </CardContent>
          </Card>

          {/* Check-in/Check-out Times */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {t("checkInTime")} / {t("checkOutTime")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("checkInTime")}</h4>
                  <p className="text-sm">{formatTime(displayProperty.check_in_time)}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("checkOutTime")}</h4>
                  <p className="text-sm">{formatTime(displayProperty.check_out_time)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status */}
          <Card>
            <CardHeader>
              <CardTitle>{t("status")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Badge variant={(displayProperty.is_active !== undefined ? displayProperty.is_active : displayProperty.active) ? "default" : "secondary"}>
                  {(displayProperty.is_active !== undefined ? displayProperty.is_active : displayProperty.active) ? t("active") : t("inactive")}
                </Badge>
                {displayProperty.featured && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Star className="h-3 w-3" />
                    {t("featured")}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t("metadata")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("createdAt")}</h4>
                  <p className="text-sm">{formatDate(displayProperty.createdAt)}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("lastUpdated")}</h4>
                  <p className="text-sm">{formatDate(displayProperty.updatedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onEdit(property)}>
              <Edit className="h-4 w-4 mr-2" />
              {t("edit")}
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => onDelete(property)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t("delete")}
            </Button>
          </div>
          <Button variant="outline" onClick={onClose}>
            {t("close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
